import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { GET_PROFILE_ATTRIBUTES } from '~/services/ProfileAttributes';
import { GET_PROFILE_VALUES, UPDATE_PROFILE_VALUE, CREATE_PROFILE_VALUE } from '~/services/ProfileValues';
import AttributeField from '../../Customer/components/AttributeField';

// Extended ProfileAttribute interface to match backend response
interface ProfileAttribute {
  _id?: string;
  id?: string;
  name: string;
  dataType: 'text' | 'number' | 'boolean' | 'date' | 'closelist' | 'openlist';
  description?: string;
  useAI: boolean;
  closelist?: {
    options: string[];
    multiple: boolean;
  };
  agent_id?: string;
  created_at?: Date;
  edit_at?: Date;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  personId: string;
  agentId: string;
  userName?: string;
}



const dialogVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20 
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
      duration: 0.3
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20
    }
  }
};

export function ProfileDialog({ isOpen, onClose, personId, agentId, userName }: Props) {
  const [t] = useTranslation(['activity', 'teams']);
  const [editedValues, setEditedValues] = useState<{[key: string]: any}>({});
  const [initialValues, setInitialValues] = useState<{values: {[key: string]: any}, ids: {[key: string]: string}}>({values: {}, ids: {}});
  const [editingAttributes, setEditingAttributes] = useState<{[key: string]: boolean}>({});
  const [saving, setSaving] = useState<{[key: string]: boolean}>({});

  const { data: attributesData, loading } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId || !isOpen,
  });

  const { data: valuesData, loading: valuesLoading, refetch: refetchValues } = useQuery(GET_PROFILE_VALUES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId || !isOpen,
  });

  const [updateProfileValue] = useMutation(UPDATE_PROFILE_VALUE);
  const [createProfileValue] = useMutation(CREATE_PROFILE_VALUE);

  // Load existing profile values when dialog opens
  useEffect(() => {
    if (valuesData?.get_profile_values?.data && !valuesLoading) {
      const profileValues = valuesData.get_profile_values.data;
      const valuesByAttribute: {[key: string]: any} = {};
      const profileValueIds: {[key: string]: string} = {};
      
      // Group values by attribute_id and person_id
      profileValues.forEach((value: any) => {
        if (value.person_id === personId) {
          const attributeKey = value.profile_attribute_id;
          valuesByAttribute[attributeKey] = value.value;
          profileValueIds[attributeKey] = value._id || value.id;
        }
      });
      
      setInitialValues({ values: valuesByAttribute, ids: profileValueIds });
      setEditedValues(valuesByAttribute);
    }
  }, [valuesData, valuesLoading, personId]);

  // Reset values when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setEditedValues({});
      setInitialValues({values: {}, ids: {}});
      setEditingAttributes({});
      setSaving({});
    } else if (isOpen && agentId) {
      // Refetch values when dialog opens to ensure fresh data
      refetchValues();
    }
  }, [isOpen, agentId, refetchValues]);

  const handleValueChange = (fieldKey: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Handle edit mode toggle
  const handleEditClick = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: true
    }));
    // Initialize edited value with current value if not already set
    setEditedValues(prev => {
      const currentValue = initialValues.values[attributeId] || '';
      return {
        ...prev,
        [attributeId]: currentValue
      };
    });
  };

  // Handle cancel edit
  const handleCancelEdit = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: false
    }));
    // Reset edited value to original
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: initialValues.values[attributeId] || ''
    }));
  };

  // Handle save individual attribute
  const handleSaveAttribute = async (attributeId: string) => {
    setSaving(prev => ({ ...prev, [attributeId]: true }));

    try {
      const newValue = editedValues[attributeId];
      const existingProfileValueId = initialValues.ids[attributeId];

      if (existingProfileValueId) {
        // Update existing value
        await updateProfileValue({
          variables: {
            data: {
              profile_value_id: existingProfileValueId,
              agent_id: agentId,
              value: newValue
            }
          }
        });
      } else {
        // Create new value
        await createProfileValue({
          variables: {
            data: {
              agent_id: agentId,
              profile_id: personId,
              attribute_id: attributeId,
              value: newValue
            }
          }
        });
      }

      // Update initial values
      setInitialValues(prev => ({
        values: { ...prev.values, [attributeId]: newValue },
        ids: { ...prev.ids }
      }));

      // Exit edit mode
      setEditingAttributes(prev => ({
        ...prev,
        [attributeId]: false
      }));

      // Refetch values to get updated data
      refetchValues();

    } catch (error) {
      console.error('Error saving attribute:', error);
    } finally {
      setSaving(prev => ({ ...prev, [attributeId]: false }));
    }
  };



  const attributes: ProfileAttribute[] = attributesData?.get_profile_attributes?.data || [];

  // Use real attributes from the query
  const finalAttributes = attributes;

  // Separate attributes by type
  const humanAttributes = finalAttributes.filter(attr => !attr.useAI);
  const aiAttributes = finalAttributes.filter(attr => attr.useAI);

  const renderAttributeSection = (attrs: ProfileAttribute[], title: string) => {
    // Sort attributes: filled ones first, then empty ones
    const sortedAttrs = [...attrs].sort((a, b) => {
      const aKey = a.id || a._id || '';
      const bKey = b.id || b._id || '';
      const aHasValue = !!initialValues.values[aKey];
      const bHasValue = !!initialValues.values[bKey];
      if (aHasValue && !bHasValue) return -1;
      if (!aHasValue && bHasValue) return 1;
      return 0;
    });

    return (
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        className="space-y-4"
      >
        <div className="flex items-center gap-2 border-b border-brand-primary/20 pb-2">
          <h3 className="text-lg font-semibold text-brand-primary">{title} {' - '} {attrs.length}</h3>
         </div>
        
        {sortedAttrs.length === 0 ? (
          <motion.div 
            variants={itemVariants}
            className="text-center py-8 text-base-content/60"
          >
            <p>{t('no_attributes_configured', { ns: 'teams' })}</p>
          </motion.div>
        ) : (
          <div className="grid gap-3">
            {sortedAttrs.map((attribute, index) => {
              const attributeKey = attribute.id || attribute._id || '';
              const isEditing = editingAttributes[attributeKey] || false;
              const currentValue = isEditing 
                ? editedValues[attributeKey] 
                : (initialValues.values[attributeKey] || '');
              
              return (
                <motion.div
                  key={attributeKey}
                  variants={itemVariants}
                  className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg hover:border-brand-primary/40 transition-colors"
                >
                  <AttributeField
                    attribute={attribute}
                    attributeId={attributeKey}
                    value={currentValue}
                    isEditing={isEditing}
                    isSaving={saving[attributeKey] || false}
                    onEdit={handleEditClick}
                    onCancel={handleCancelEdit}
                    onSave={handleSaveAttribute}
                    onChange={handleValueChange}
                    displayMode="inline"
                    showAIIndicator={false}
                    showTypeIndicator={true}
                  />
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal modal-open">
        <motion.div
          className="modal-box max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          variants={dialogVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b border-brand-primary/20 pb-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-base-content">
                {userName } 
              </h2>
              
            </div>
            <button 
              className="btn btn-sm btn-circle btn-ghost hover:bg-brand-primary/10"
              onClick={onClose}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {(loading || valuesLoading) ? (
              <div className="flex justify-center items-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <div className="space-y-8">
                {humanAttributes.length > 0 && (
                  <div>
                    {renderAttributeSection(
                      humanAttributes,
                      t('human_managed_fields', { ns: 'teams' })
                    )}
                  </div>
                )}
                
                {aiAttributes.length > 0 && (
                  <div>
                    {renderAttributeSection(
                      aiAttributes,
                      t('ai_inferred_fields', { ns: 'teams' })
                    )}
                  </div>
                )}

                {finalAttributes.length === 0 && (
                  <div className="text-center py-12">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-base-content mb-2">
                        {t('no_attributes_title', { ns: 'teams' })}
                      </h3>
                      <p className="text-base-content/70 mb-6">
                        {t('no_attributes_message', { ns: 'teams' })}
                      </p>
                    </div>
                    <button 
                      className="btn btn-primary rounded-full"
                      onClick={() => {
                        window.open(`http://localhost:5173/agent/${agentId}/teams/profile`, '_blank');
                      }}
                    >
                      {t('configure_attributes', { ns: 'teams' })}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-brand-primary/20 pt-4 mt-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-base-content/60">
                {finalAttributes.length > 0 && (
                  <span>{t('total_attributes', { ns: 'teams' })}: {finalAttributes.length}</span>
                )}
              </div>
              <div className="flex gap-3">
                <button 
                  className="btn btn-ghost rounded-full"
                  onClick={onClose}
                >
                  {t('cancel', { ns: 'activity' })}
                </button>
                <a 
                  className="btn btn-primary rounded-full"
                  href={`/agent/${agentId}/customers/${personId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  style={{ pointerEvents: Object.values(saving).some(isSaving => isSaving) ? 'none' : 'auto' }}
                >
                  {Object.values(saving).some(isSaving => isSaving) ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      {t('saving', { ns: 'teams' })}
                    </>
                  ) : (
                    t('view_profile', { ns: 'activity' })
                  )}
                </a>
              </div>
            </div>
          </div>
        </motion.div>
        <div className="modal-backdrop" onClick={onClose} />
      </div>
    </AnimatePresence>
  );
}
